import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Building2, Clock, MessageCircle, Award, Coins, KeyRound, Info } from "lucide-react";

const COMPANY_IMG = "https://images.unsplash.com/photo-1664408763928-efbce49f7857?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHx3aW5kJTIwdHVyYmluZSUyMGZvcmVzdHxlbnwwfHx8fDE3ODYzNzA0MTR8MA&ixlib=rb-4.1.0&q=85";

export default function Companies() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [responses, setResponses] = useState([]);
  const [replyBody, setReplyBody] = useState("");

  const load = () => api.get("/company/requests").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openReq = async (r) => {
    setSelected(r);
    const resp = await api.get(`/company/responses/${r.id}`);
    setResponses(resp.data);
  };

  const respond = async () => {
    if (!user) return toast.error("Sign in to respond");
    if (replyBody.length < 5) return;
    try {
      await api.post("/company/responses", { request_id: selected.id, body: replyBody });
      setReplyBody("");
      openReq(selected);
      load();
      toast.success("Response posted");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const accept = async (respId) => {
    try {
      const { data } = await api.post("/company/responses/accept", { response_id: respId });
      toast.success(`Response accepted — +${data.awarded} points awarded`);
      openReq(selected);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-16">
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mono text-xs text-primary">// 03 · company requests</div>
          <h1 className="mt-2 text-5xl font-black tracking-tighter">Real challenges. Real answers.</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">Companies post ecological improvement challenges. The community answers with ideas, data, and solutions. Some carry a live bounty.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {user?.role !== "company" && (
            <Link to="/companies/portal" data-testid="companies-cta-signup" className="border border-primary bg-primary/10 hover:bg-primary/20 px-4 py-2 text-xs uppercase tracking-widest text-primary flex items-center gap-2">
              <KeyRound className="h-3 w-3" />Are you a company? Sign up now and post enquiries!
            </Link>
          )}
          {user?.role === "company" && (
            <Link to="/companies/portal"><Button variant="outline" data-testid="companies-portal-link" className="rounded-none"><Building2 className="mr-2 h-4 w-4" />Open portal</Button></Link>
          )}
        </div>
      </div>

      <div className="mb-8 border border-primary/40 bg-primary/5 p-5">
        <div className="flex items-center gap-2 text-primary text-xs uppercase tracking-widest"><Info className="h-3 w-3" />How bounties work</div>
        <div className="mt-3 grid gap-4 md:grid-cols-3 text-sm">
          <div>
            <div className="font-bold tracking-tight">1. Companies post a challenge</div>
            <div className="mt-1 text-muted-foreground">Verified companies attach a <span className="text-primary">cash bounty</span> (any amount, USD/EUR/GBP/INR) when they post an enquiry, idea request, or issue.</div>
          </div>
          <div>
            <div className="font-bold tracking-tight">2. Funds held in Stripe escrow</div>
            <div className="mt-1 text-muted-foreground">Bounty is charged upfront via Stripe Checkout and held. A <span className="text-primary">5% platform fee</span> is added on top and covers hosting, moderation and payout infra.</div>
          </div>
          <div>
            <div className="font-bold tracking-tight">3. Responder gets paid on accept</div>
            <div className="mt-1 text-muted-foreground">When the company accepts a response, the full bounty is transferred to the responder's connected Stripe account. Responders connect Stripe in <Link to="/settings" className="text-primary hover:underline">Settings → Payouts</Link>.</div>
          </div>
        </div>
        <div className="mt-4 text-[10px] uppercase tracking-widest text-muted-foreground">All amounts are shown net of the platform fee to the responder. Fees are non-refundable if a challenge is not solved.</div>
      </div>

      {selected ? (
        <div>
          <button onClick={() => setSelected(null)} className="mono text-xs text-muted-foreground hover:text-primary mb-4">← all requests</button>
          <div className="border border-border p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-xs text-primary uppercase tracking-widest"><Building2 className="h-3 w-3" />{selected.company}</div>
                <h2 className="mt-2 text-3xl font-black tracking-tighter">{selected.title}</h2>
                {selected.bounty_amount && (
                  <div className="mt-3 inline-flex items-center gap-1 border border-primary bg-primary/10 text-primary px-3 py-1 uppercase tracking-widest text-xs">
                    <Coins className="h-3 w-3" />bounty · {selected.bounty_currency || "USD"} {selected.bounty_amount}
                  </div>
                )}
              </div>
              <div className="text-right text-xs text-muted-foreground">
                {selected.budget && <div className="mono">{selected.budget}</div>}
                {selected.deadline && <div className="mt-1 flex items-center gap-1 justify-end"><Clock className="h-3 w-3" />{selected.deadline}</div>}
              </div>
            </div>
            <p className="mt-4 whitespace-pre-wrap leading-relaxed">{selected.description}</p>
          </div>

          <div className="mt-8">
            <div className="mb-4 flex items-center gap-2 text-sm uppercase tracking-widest text-primary"><MessageCircle className="h-4 w-4" />Responses ({responses.length})</div>
            {user && (
              <div className="border border-border p-4 mb-6">
                <Textarea data-testid="response-body" rows={3} value={replyBody} onChange={(e) => setReplyBody(e.target.value)} placeholder="Share your idea, data, or solution…" className="rounded-none bg-input mb-3" />
                <Button onClick={respond} data-testid="response-submit" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90">Post response</Button>
              </div>
            )}
            <div className="space-y-2">
              {responses.map((r) => (
                <div key={r.id} data-testid={`response-${r.id}`} className={`border p-4 ${r.accepted ? "border-primary bg-primary/5" : "border-border"}`}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold">{r.author_name} <span className="text-primary uppercase tracking-widest">· {r.author_role}</span></span>
                    <div className="flex items-center gap-3">
                      <span className="mono text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
                      {r.accepted && <span className="border border-primary px-2 py-0.5 uppercase tracking-widest text-[9px] text-primary">accepted · +100 pts</span>}
                    </div>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed whitespace-pre-wrap">{r.body}</p>
                  {user && selected.author_id === user.id && !r.accepted && (
                    <Button data-testid={`accept-${r.id}`} size="sm" onClick={() => accept(r.id)} className="mt-3 rounded-none bg-primary text-primary-foreground hover:bg-primary/90"><Award className="mr-2 h-3 w-3" />Accept & award 100 pts</Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center">
          <img src={COMPANY_IMG} alt="" className="mx-auto mb-6 h-48 w-full max-w-lg border border-primary/40 object-cover grayscale" />
          <div className="text-lg font-bold">No open requests</div>
          <div className="mt-2 text-sm text-muted-foreground">Be the first company to post — or invite one.</div>
        </div>
      ) : (
        <div className="grid gap-px bg-border md:grid-cols-2">
          {items.map((r) => (
            <button key={r.id} data-testid={`req-card-${r.id}`} onClick={() => openReq(r)} className={`bg-background p-6 text-left hard-hover ${r.status === "solved" ? "ring-1 ring-primary/60" : ""}`}>
              <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1 text-primary uppercase tracking-widest"><Building2 className="h-3 w-3" />{r.company}</span>
                <span className="mono text-muted-foreground">{r.response_count} replies</span>
              </div>
              <h3 className="mt-3 text-xl font-bold tracking-tight">{r.title}</h3>
              <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{r.description}</p>
              <div className="mt-4 flex items-center justify-between text-xs">
                <div className="flex flex-wrap items-center gap-2">
                  {r.bounty_amount && (
                    <span className="border border-primary bg-primary/10 text-primary px-2 py-0.5 flex items-center gap-1 uppercase tracking-widest text-[9px]">
                      <Coins className="h-3 w-3" />{r.bounty_currency || "USD"} {r.bounty_amount}
                    </span>
                  )}
                  {r.budget && <span className="mono text-muted-foreground">{r.budget}</span>}
                </div>
                <div className="flex items-center gap-2">
                  {r.status === "solved" && <span className="text-primary uppercase tracking-widest text-[9px]">solved</span>}
                  {r.deadline && <span className="text-muted-foreground">due {r.deadline}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
